import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Plus, CheckSquare, Loader2, AlertTriangle, Sparkles, Trash2, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Checkbox } from '../ui/checkbox';
import { LoadingSpinner } from '../ui/LoadingSpinner';
import { TaskForm } from './TaskForm';
import { TaskCard } from './TaskCard';
import { TaskFilters } from './TaskFilters';
import { TaskAICreationModal } from './TaskAICreationModal';
import {
  Task,
  TaskStatistics,
  CreateTaskRequest,
  UpdateTaskRequest,
  TaskArea,
  TaskPriority,
  TaskStatus
} from '../../types/task';
import { getTasks, createTask, updateTask, deleteTask, getServiceOrders, bulkDeleteTasks } from '../../services/api';
import { socketService } from '../../services/socket';
import { usePermissions } from '../../contexts/PermissionsContext';

interface TasksTabProps {
  projectId: string;
}

export const TasksTab: React.FC<TasksTabProps> = ({ projectId }) => {
  const { canManage } = usePermissions();
  const canManageTasks = canManage('tasks');

  const [tasks, setTasks] = useState<Task[]>([]);
  const [statistics, setStatistics] = useState<TaskStatistics>({
    total: 0,
    pendente: 0,
    em_progresso: 0,
    pausado: 0,
    encerrada: 0,
    totalHoras: 0,
    horasCompletas: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [filters, setFilters] = useState<{
    status?: TaskStatus;
    prioridade?: TaskPriority;
    area?: TaskArea;
    unassigned?: boolean;
    sortBy?: 'newest' | 'oldest';
  }>({});
  const [hasServiceOrders, setHasServiceOrders] = useState<boolean | null>(null);
  const [initialFilterSet, setInitialFilterSet] = useState(false);
  const [selectedTasks, setSelectedTasks] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);

  // Selection handlers
  const handleSelectTask = (taskId: string) => {
    setSelectedTasks(prev => {
      const newSet = new Set(prev);
      if (newSet.has(taskId)) {
        newSet.delete(taskId);
      } else {
        newSet.add(taskId);
      }
      return newSet;
    });
  };

  const handleSelectAll = () => {
    if (selectedTasks.size === tasks.length) {
      setSelectedTasks(new Set());
    } else {
      setSelectedTasks(new Set(tasks.map(t => t._id)));
    }
  };

  const clearSelection = () => {
    setSelectedTasks(new Set());
  };

  // Bulk delete handler
  const handleBulkDelete = async () => {
    if (selectedTasks.size === 0) return;

    const count = selectedTasks.size;
    if (!window.confirm(`Tem certeza que deseja excluir ${count} tarefa${count > 1 ? 's' : ''}?`)) {
      return;
    }

    try {
      setBulkLoading(true);
      await bulkDeleteTasks(projectId, Array.from(selectedTasks));
      setSelectedTasks(new Set());
      await loadTasks();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir tarefas');
    } finally {
      setBulkLoading(false);
    }
  };

  // Carregar tarefas
  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getTasks(projectId, filters);
      const { tasks: taskList, statistics: stats } = response.data.data;

      // Apply client-side sorting by creation date
      const sortBy = filters.sortBy || 'newest';
      const sortedTasks = [...taskList].sort((a, b) => {
        const dateA = new Date(a.createdAt).getTime();
        const dateB = new Date(b.createdAt).getTime();
        return sortBy === 'oldest' ? dateA - dateB : dateB - dateA;
      });

      setTasks(sortedTasks);
      setStatistics(stats);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao carregar tarefas');
      console.error('Erro ao carregar tarefas:', err);
    } finally {
      setLoading(false);
    }
  };

  // Verificar se existem service orders no projeto
  const checkServiceOrders = async () => {
    try {
      const response = await getServiceOrders(projectId);
      const serviceOrders = response.data.data;
      const hasOrders = serviceOrders && serviceOrders.length > 0;
      setHasServiceOrders(hasOrders);
      
      // Definir filtro padrão inteligente apenas uma vez
      if (!initialFilterSet) {
        if (hasOrders) {
          // Se existem OSs, filtrar para mostrar tarefas não vinculadas
          setFilters({ unassigned: true });
        }
        // Se não existem OSs, manter filtro vazio (mostrar todas)
        setInitialFilterSet(true);
      }
    } catch (err) {
      console.error('Erro ao verificar service orders:', err);
      setHasServiceOrders(false);
      if (!initialFilterSet) {
        setInitialFilterSet(true);
      }
    }
  };

  // Efeito para verificar service orders no mount
  useEffect(() => {
    checkServiceOrders();
  }, [projectId]);

  // Efeito para carregar tarefas quando filtros mudam ou após definir filtro inicial
  useEffect(() => {
    if (initialFilterSet) {
      loadTasks();
    }
  }, [projectId, filters, initialFilterSet]);

  // Socket handlers para atualizações em tempo real
  useEffect(() => {
    // Conectar ao socket e entrar na sala do projeto
    socketService.connect();
    socketService.joinProject(projectId);

    // Handler para atualização JIRA de tarefas
    const handleTaskJiraUpdate = (updatedTask: Task) => {
      setTasks(currentTasks => 
        currentTasks.map(task => 
          task._id === updatedTask._id ? { ...task, ...updatedTask } : task
        )
      );
    };

    // Handler para atualização geral de tarefas
    const handleTaskUpdate = (updatedTask: Task) => {
      setTasks(currentTasks => 
        currentTasks.map(task => 
          task._id === updatedTask._id ? updatedTask : task
        )
      );
    };

    // Handler para criação de tarefas
    const handleTaskCreated = (newTask: Task) => {
      // Recarregar para manter filtros e estatísticas atualizados
      loadTasks();
    };

    // Handler para exclusão de tarefas
    const handleTaskDeleted = (taskId: string) => {
      setTasks(currentTasks => currentTasks.filter(task => task._id !== taskId));
    };

    // Registrar event listeners
    socketService.on('task-jira-updated', handleTaskJiraUpdate);
    socketService.on('task-updated', handleTaskUpdate);
    socketService.on('task-created', handleTaskCreated);
    socketService.on('task-deleted', handleTaskDeleted);

    // Cleanup na desmontagem
    return () => {
      socketService.off('task-jira-updated', handleTaskJiraUpdate);
      socketService.off('task-updated', handleTaskUpdate);
      socketService.off('task-created', handleTaskCreated);
      socketService.off('task-deleted', handleTaskDeleted);
      socketService.leaveProject(projectId);
    };
  }, [projectId]);

  // Criar nova tarefa
  const handleCreateTask = async (taskData: CreateTaskRequest) => {
    try {
      setFormLoading(true);
      await createTask(projectId, taskData);
      await loadTasks(); // Recarregar lista
      // Verificar novamente service orders caso tenha sido criada a primeira tarefa
      await checkServiceOrders();
      setShowForm(false);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao criar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  // Atualizar tarefa
  const handleUpdateTask = async (taskData: UpdateTaskRequest) => {
    if (!editingTask) return;
    
    try {
      setFormLoading(true);
      await updateTask(projectId, editingTask._id, taskData);
      await loadTasks(); // Recarregar lista
      setEditingTask(null);
    } catch (err: any) {
      throw new Error(err.response?.data?.message || 'Erro ao atualizar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  // Excluir tarefa
  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Tem certeza que deseja excluir esta tarefa?')) {
      return;
    }

    try {
      await deleteTask(projectId, taskId);
      await loadTasks(); // Recarregar lista
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao excluir tarefa');
    }
  };

  // Alterar status da tarefa
  const handleStatusChange = async (taskId: string, newStatus: TaskStatus) => {
    try {
      await updateTask(projectId, taskId, { status: newStatus });
      await loadTasks(); // Recarregar lista
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao alterar status da tarefa');
    }
  };

  // Duplicar tarefa
  const handleDuplicateTask = async (task: Task) => {
    try {
      const duplicatedTask: CreateTaskRequest = {
        name: `${task.name} (Cópia)`,
        description: task.description,
        area: task.area,
        horasEstimadas: task.horasEstimadas,
        prioridade: task.prioridade,
        status: 'pendente'
      };
      
      setFormLoading(true);
      await createTask(projectId, duplicatedTask);
      await loadTasks();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao duplicar tarefa');
    } finally {
      setFormLoading(false);
    }
  };

  // Limpar filtros
  const handleClearFilters = () => {
    setFilters({});
  };

  if (loading || !initialFilterSet) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner message="Carregando tarefas" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Error Message */}
      {error && (
        <Card variant="glass" className="border-red-500/30 bg-red-500/10">
          <CardContent className="p-4 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            <span className="text-red-400">{error}</span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setError(null)}
              className="ml-auto text-red-400 hover:text-red-300"
            >
              ✕
            </Button>
          </CardContent>
        </Card>
      )}


      {/* Filtros */}
      <TaskFilters
        filters={filters}
        onFilterChange={setFilters}
        onClearFilters={handleClearFilters}
      />

      {/* Bulk Actions Toolbar - Rendered via Portal to ensure fixed positioning works */}
      {canManageTasks && selectedTasks.size > 0 && createPortal(
        <div
          style={{
            position: 'fixed',
            bottom: '24px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 99999,
          }}
        >
          <div className="bg-gray-900/95 backdrop-blur-md shadow-2xl border border-blue-500/30 rounded-lg p-3 px-6">
            <div className="flex items-center gap-6">
              <span className="text-sm text-foreground-secondary whitespace-nowrap">
                {selectedTasks.size} tarefa{selectedTasks.size > 1 ? 's' : ''} selecionada{selectedTasks.size > 1 ? 's' : ''}
              </span>

              <div className="h-4 w-px bg-white/20" />

              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleSelectAll}
                  className="text-foreground-secondary hover:text-foreground-primary"
                >
                  <CheckSquare className="w-4 h-4 mr-1" />
                  Selecionar todas
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearSelection}
                  className="text-foreground-tertiary hover:text-foreground-primary"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpar
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={bulkLoading}
                  className="bg-red-500 hover:bg-red-600 text-white"
                >
                  {bulkLoading ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-1" />
                  )}
                  Excluir selecionadas
                </Button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-heading text-foreground-primary">
          Lista de Tarefas
          {tasks.length > 0 && (
            <span className="text-foreground-tertiary ml-2">({tasks.length})</span>
          )}
        </h2>
        <div className="flex items-center gap-2">
          {canManageTasks && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAIModal(true)}
                className="text-purple-400 border-purple-500/30 hover:bg-purple-500/10"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                Criar tarefas por IA
              </Button>
              <Button
                variant="gradient"
                size="sm"
                onClick={() => setShowForm(true)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Nova Tarefa
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Task List */}
      <div className="space-y-3">
        {tasks.length === 0 ? (
          <Card variant="glass">
            <CardContent className="p-8 text-center">
              <CheckSquare className="w-12 h-12 text-foreground-tertiary mx-auto mb-4" />
              <h3 className="text-lg font-heading text-foreground-primary mb-2">
                {Object.values(filters).some(f => f) ? 'Nenhuma tarefa encontrada' : 'Nenhuma tarefa criada'}
              </h3>
              <p className="text-foreground-tertiary mb-4">
                {Object.values(filters).some(f => f) 
                  ? 'Tente ajustar os filtros para encontrar outras tarefas'
                  : 'Comece criando sua primeira tarefa para este projeto'
                }
              </p>
              {!Object.values(filters).some(f => f) && canManageTasks && (
                <Button
                  variant="gradient"
                  onClick={() => setShowForm(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Criar Primeira Tarefa
                </Button>
              )}
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {tasks.map((task) => (
              <TaskCard
                key={task._id}
                task={task}
                onEdit={canManageTasks ? setEditingTask : undefined}
                onDelete={canManageTasks ? handleDeleteTask : undefined}
                onStatusChange={handleStatusChange}
                onDuplicate={canManageTasks ? handleDuplicateTask : undefined}
                showSelection={canManageTasks}
                isSelected={selectedTasks.has(task._id)}
                onSelect={handleSelectTask}
              />
            ))}
          </div>
        )}
      </div>

      {/* Task Form Modal */}
      {(showForm || editingTask) && (
        <TaskForm
          task={editingTask}
          onSubmit={editingTask ? handleUpdateTask : handleCreateTask}
          onCancel={() => {
            setShowForm(false);
            setEditingTask(null);
          }}
          loading={formLoading}
        />
      )}

      {/* AI Task Creation Modal */}
      <TaskAICreationModal
        isOpen={showAIModal}
        onClose={() => setShowAIModal(false)}
        onTasksCreated={(count) => {
          // Reload tasks after AI creation
          loadTasks();
          setShowAIModal(false);
        }}
        projectId={projectId}
      />
    </div>
  );
};